import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Plus, Minus, Check, Zap, Receipt, Sparkles, Settings } from 'lucide-react';
import { getSuggestedCreators, setupDonations, getMyDonationAllocations, triggerAutoAllocation } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { MonthlyReceipt } from '../components/MonthlyReceipt';
import { UndiscoveredBoostSettings } from '../components/UndiscoveredBoostSettings';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

function DonationSetupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [suggestedCreators, setSuggestedCreators] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [existingAllocations, setExistingAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('automatic');
  const [autoAllocating, setAutoAllocating] = useState(false);
  
  const MAX_DONATION = 2.00;

  useEffect(() => {
    async function fetchData() {
      try {
        const [creatorsRes, allocationsRes] = await Promise.all([
          getSuggestedCreators(12),
          getMyDonationAllocations()
        ]);
        setSuggestedCreators(creatorsRes.data);
        setExistingAllocations(allocationsRes.data);
        
        const initialAllocations = {};
        allocationsRes.data.forEach(a => {
          initialAllocations[a.creator_id] = a.amount;
        });
        setAllocations(initialAllocations);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const totalAllocated = Object.values(allocations).reduce((sum, val) => sum + val, 0);
  const remaining = MAX_DONATION - totalAllocated;

  const updateAllocation = (creatorId, delta) => {
    const current = allocations[creatorId] || 0;
    const newValue = Math.max(0, Math.min(current + delta, current + remaining));
    
    if (newValue === 0) {
      const newAllocations = { ...allocations };
      delete newAllocations[creatorId];
      setAllocations(newAllocations);
    } else {
      setAllocations({ ...allocations, [creatorId]: Math.round(newValue * 100) / 100 });
    }
  };

  const handleAutoAllocate = async () => {
    try {
      setAutoAllocating(true);
      const res = await triggerAutoAllocation();
      toast.success('Auto-allocation set based on your reading!');
      
      // Update local state with new allocations
      const newAllocations = {};
      (res.data.allocations || []).forEach(a => {
        newAllocations[a.creator_id] = a.amount;
      });
      setAllocations(newAllocations);
    } catch (error) {
      toast.error('Failed to auto-allocate. Keep reading comics first!');
    } finally {
      setAutoAllocating(false);
    }
  };

  const handleSubmit = async () => {
    const allocationsList = Object.entries(allocations).map(([creatorId, amount]) => ({
      creator_id: creatorId,
      amount
    }));

    if (allocationsList.length < 3) {
      toast.error('Please select at least 3 creators');
      return;
    }

    try {
      setSubmitting(true);
      await setupDonations({ allocations: allocationsList });
      toast.success('Donation preferences saved!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-12 w-64 bg-[var(--border)] rounded" />
            <div className="h-8 w-full bg-[var(--border)] rounded" />
            <div className="grid grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="h-24 bg-[var(--border)] rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 pb-20" data-testid="donation-setup-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-teko mb-2">Support Creators</h1>
          <p className="text-[var(--text-secondary)] max-w-xl mx-auto">
            Your $2/month helps creators continue making amazing comics. 
            Choose how to distribute your support.
          </p>
        </div>

        {/* Philosophy Banner */}
        <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/20 rounded-lg p-4 mb-8 text-center">
          <p className="text-sm italic">
            "This isn't tipping. It's revenue sharing based on what you actually enjoy."
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[var(--border)] pb-2">
          <button
            onClick={() => setActiveTab('automatic')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'automatic' 
                ? 'bg-[var(--primary)] text-white' 
                : 'text-[var(--text-secondary)] hover:bg-[var(--surface)]'
            }`}
          >
            <Zap size={18} />
            Smart Auto
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'manual' 
                ? 'bg-[var(--primary)] text-white' 
                : 'text-[var(--text-secondary)] hover:bg-[var(--surface)]'
            }`}
          >
            <Settings size={18} />
            Manual
          </button>
          <button
            onClick={() => setActiveTab('receipt')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'receipt' 
                ? 'bg-[var(--primary)] text-white' 
                : 'text-[var(--text-secondary)] hover:bg-[var(--surface)]'
            }`}
          >
            <Receipt size={18} />
            Receipt
          </button>
          <button
            onClick={() => setActiveTab('boost')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'boost' 
                ? 'bg-[var(--primary)] text-white' 
                : 'text-[var(--text-secondary)] hover:bg-[var(--surface)]'
            }`}
          >
            <Sparkles size={18} />
            Boost
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'automatic' && (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[var(--primary)]/20 flex items-center justify-center mx-auto mb-4">
                <Zap size={32} className="text-[var(--primary)]" />
              </div>
              <h2 className="text-2xl font-teko mb-2">Smart Automatic Allocation</h2>
              <p className="text-[var(--text-secondary)] max-w-md mx-auto">
                Your $2 is automatically split based on how much you read each creator.
                No thinking required - just enjoy comics!
              </p>
            </div>

            <div className="bg-[var(--background)] rounded-lg p-4 mb-6">
              <h3 className="font-medium mb-3">How it works:</h3>
              <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-500 mt-0.5" />
                  <span>We track what you read (pages, likes, chapter completions)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-500 mt-0.5" />
                  <span>Creators you engage with more get a larger share</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-500 mt-0.5" />
                  <span>Min $0.20 floor protects small creators you read</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-500 mt-0.5" />
                  <span>Max 60% cap ensures diverse support</span>
                </li>
              </ul>
            </div>

            <Button 
              onClick={handleAutoAllocate} 
              disabled={autoAllocating}
              className="w-full"
            >
              {autoAllocating ? 'Calculating from your reading...' : 'Enable Smart Allocation'}
            </Button>

            <p className="text-xs text-center text-[var(--text-secondary)] mt-4">
              You can switch to manual allocation anytime
            </p>
          </div>
        )}

        {activeTab === 'manual' && (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
            {/* Balance Display */}
            <div className="flex justify-between items-center mb-6 p-4 bg-[var(--background)] rounded-lg">
              <div>
                <p className="text-sm text-[var(--text-secondary)]">Monthly Budget</p>
                <p className="text-2xl font-teko">${MAX_DONATION.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-[var(--text-secondary)]">Allocated</p>
                <p className="text-2xl font-teko text-[var(--primary)]">${totalAllocated.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-[var(--text-secondary)]">Remaining</p>
                <p className={`text-2xl font-teko ${remaining > 0 ? 'text-green-500' : 'text-[var(--text-secondary)]'}`}>
                  ${remaining.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Creator Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {suggestedCreators.map(creator => {
                const amount = allocations[creator.id] || 0;
                const isSelected = amount > 0;

                return (
                  <div
                    key={creator.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isSelected 
                        ? 'border-[var(--primary)] bg-[var(--primary)]/5' 
                        : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-[var(--background)] overflow-hidden">
                        {creator.profile_picture ? (
                          <img src={creator.profile_picture} alt={creator.username} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xl font-bold text-[var(--text-secondary)]">
                            {creator.username?.[0] || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{creator.username}</p>
                        <p className="text-xs text-[var(--text-secondary)]">
                          {creator.comic_count} comics
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateAllocation(creator.id, -0.10)}
                          disabled={amount === 0}
                          className="w-8 h-8 rounded-full bg-[var(--background)] flex items-center justify-center disabled:opacity-50"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-16 text-center font-bold">
                          ${amount.toFixed(2)}
                        </span>
                        <button
                          onClick={() => updateAllocation(creator.id, 0.10)}
                          disabled={remaining <= 0}
                          className="w-8 h-8 rounded-full bg-[var(--background)] flex items-center justify-center disabled:opacity-50"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      {isSelected && <Heart size={18} className="text-[var(--primary)]" fill="currentColor" />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(allocations).length < 3}
              className="w-full"
            >
              {submitting ? 'Saving...' : 'Save Manual Allocation'}
            </Button>

            {Object.keys(allocations).length < 3 && (
              <p className="text-sm text-center text-yellow-500 mt-2">
                Select at least 3 creators to continue
              </p>
            )}
          </div>
        )}

        {activeTab === 'receipt' && (
          <MonthlyReceipt />
        )}

        {activeTab === 'boost' && (
          <UndiscoveredBoostSettings />
        )}
      </div>
    </div>
  );
}

export default DonationSetupPage;
